import i18n from '@/plugins/i18n'
import { getI18n } from '@/plugins/i18n'

import { useLookup, useDefaultDialogTitle } from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'

import { useGarageStore } from '@/stores/garage.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useWebSiteStore } from '@/stores/web-site.store'

export const useDialogTitle = () => {
  const { t } = i18n.global
  const { defaultActionTitle } = useDefaultDialogTitle()

  const itemTitle = (i) => i ? `${i.number_mnemonic}/${i.series} ${i.customer.name}` : ''

  const dialogTitle = (i) => {
    const title = `${defaultActionTitle(i)} ${t('common.serviceHistory')}`
    return i.id ? `${title} - ${itemTitle(i)}` : title
  }

  return { dialogTitle, itemTitle }
}

export const useColumns = (editors, currentCountryId = {}) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()
  const webSiteStore = useWebSiteStore()
  const garageStore = useGarageStore()

  const getFilteredStates = () => {
    return {
      store: webSiteStore.states,
      filter: currentCountryId?.value ? ['country', '=', currentCountryId.value] : null,
      sort: 'name',
    }
  }

  const getSubjectTypes = (options) => {
    return {
      store: registryStore.getLocalizedSubjectTypes(),
      filter: ['parent_id', '=', null],
      sort: 'name',
    }
  }

  const getSubjectSubTypes = (option) => {
    return {
      store: registryStore.getLocalizedSubjectTypes(),
      filter: ['parent_id', '=', option.data?.customer?.subject_type_ref || 0],
      sort: 'name',
    }
  }

  return [
    {
      caption: i18n.t('common.chassisCode'),
      dataField: 'vehicle_vin',
      sortOrder: 'asc',
    },
    dataGridColumn.useSeriesColumn(),
    dataGridColumn.useNumberMnemonicColumn(),
    dataGridColumn.useDateColumn(),
    {
      dataField: 'vehicle_km',
      caption: i18n.t('common.km'),
      dataType: 'number',
      width: '5rem',
    },
    dataGridColumn.useSupplierBrandColumn({ dataField: 'vehicle_supplier_brand_ref', all: true}),
    dataGridColumn.useCustomerColumn({
      dataField: 'customer_id',
      editCellTemplate: 'customerEditor',
      visible: false,
    }),
    {
      dataField: 'customer.subject_type_ref',
      caption: i18n.t('common.subjectType'),
      dataType: 'number',
      lookup: useLookup({ dataSource: getSubjectTypes }),
      visible: false,
    },
    {
      dataField: 'customer.subject_sub_type_ref',
      caption: i18n.t('common.subjectSubType'),
      dataType: 'number',
      lookup: useLookup({ dataSource: getSubjectSubTypes }),
      visible: false,
    },
    {
      caption: i18n.t('common.country'),
      dataField: 'customer.address.country',
      editCellTemplate: 'countryEditor',
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.state'),
      dataField: 'customer.address.state',
      lookup: useLookup({ dataSource: getFilteredStates }),
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.city'),
      dataField: 'customer.address.city',
      editCellTemplate: 'cityEditor',
      visible: false,
      showInColumnChooser: false,
    },
    { dataField: 'customer.code', visible: false, caption: `${i18n.t('common.customer')} ${i18n.t('common.code')}`},
    { dataField: 'customer.name', visible: false, caption: `${i18n.t('common.customer')} ${i18n.t('common.nameAlt')}`},
    { dataField: 'customer.name_ext', visible: false, caption: `${i18n.t('common.customer')} ${i18n.t('common.nameExt')}`},
    {
      caption: `${i18n.t('common.customer')} ${i18n.t('common.vatCountry')}`,
      dataField: 'customer.vat_country',
      editCellTemplate: 'countryEditor',
      visible: false,
      showInColumnChooser: false,
    },
    { dataField: 'customer.vat_number', visible: false, caption: `${i18n.t('common.customer')} ${i18n.t('common.vatNumber')}`},
    { dataField: 'customer.tax_code', visible: false, caption: `${i18n.t('common.customer')} ${i18n.t('common.taxCode')}`},
    { dataField: 'customer.address.country_name', visible: false, caption: `${i18n.t('common.customer')} ${i18n.t('common.country')}`},
    { dataField: 'customer.address.state_name', visible: false, caption: `${i18n.t('common.customer')} ${i18n.t('common.state')}`},
    { dataField: 'customer.address.city_name', visible: false, caption: `${i18n.t('common.customer')} ${i18n.t('common.city')}`},
    { dataField: 'customer.address.zip_code', visible: false, caption: `${i18n.t('common.customer')} ${i18n.t('common.zipCode')}`},
    { dataField: 'customer.address.street', visible: false, caption: `${i18n.t('common.customer')} ${i18n.t('common.street')}`},
    dataGridColumn.useEmailColumn({ dataField: 'customer.email', caption: `${i18n.t('common.customer')} ${i18n.t('common.email')}`}),
    { dataField: 'customer.mobile', visible: false, caption: `${i18n.t('common.customer')} ${i18n.t('common.mobile')}`},
    { dataField: 'customer.phone', visible: false, caption: `${i18n.t('common.customer')} ${i18n.t('common.phone')}`},
    {
      dataField: 'garage',
      editCellTemplate: 'depositEditor',
      caption: i18n.t('common.garage'),
      lookup: useLookup({ store: registryStore.getLocalizedDeposits() }),
    },
    dataGridColumn.useDepositColumn(),
    {
      caption: i18n.t('common.workReason'),
      dataField: 'garage_reason_ref',
      lookup: useLookup({ store: garageStore.getLocalizedGarageReasons() }),
    },
    {
      caption: i18n.t('common.interventionType'),
      lookup: useLookup({ store: garageStore.getLocalizedInterventionTypes() }),
      dataField: 'intervention_type_ref',
      editorOptions: {
        placeholder: ''
      }
    },
    {
      caption: i18n.t('common.plate'),
      dataField: 'vehicle_plate_number',
    },
    {
      caption: i18n.t('common.mechanic'),
      dataField: 'mechanics',
      cellTemplate: 'mechanicsAvatarList',
      lookup: useLookup({
        store: webSiteStore.userDetails.filter(i => i.is_mechanic),
        sort: 'name',
        displayExpr: (item) => `${item.name} ${item.name_ext}`,
      }),
      minWidth: 115,
    },
    {
      caption: i18n.t('common.carServiceNumber'),
      dataField: 'car_service',
      editorOptions: {
        placeholder: ''
      },
      visible: false,
    },
    {
      caption: i18n.t('common.workType'),
      dataField: 'work_type',
      lookup: useLookup({
        store: garageStore.getLocalizedWorkTypes(),
        displayExpr: (item) => item.name
      }),
      visible: false,
      validationRules: [{ type: 'required' }]
    },
    {
      caption: i18n.t('common.workValue'),
      dataField: 'work_value',
      lookup: useLookup({
        store: garageStore.getLocalizedWorkValues(),
        displayExpr: (item) => item.name
      }),
      visible: false,
    },
    {
      caption: i18n.t('common.engineCode'),
      dataField: 'engine_code',
      showInColumnChooser: false,
      visible: false,
    },
    {
      caption: i18n.t('common.color'),
      dataField: 'color',
      visible: false,
    },
    dataGridColumn.useEngineSizeColumn({ dataField: 'vehicle.engine_size' }),
    {
      caption: i18n.t('common.enrollmentDate'),
      dataField: 'enrollment_date',
      dataType: 'date',
      visible: false,
    },
    {
      caption: i18n.t('common.saleDate'),
      dataField: 'sale_date',
      dataType: 'date',
      visible: false,
    },
    {
      caption: i18n.t('common.revisionDueDate'),
      dataField: 'revision_due_date',
      dataType: 'date',
      visible: false,
    },
    {
      caption: i18n.t('common.revisionNextDate'),
      dataField: 'revision_next_date',
      dataType: 'date',
      visible: false,
    },
    dataGridColumn.usePaymentTypeColumn(),
    dataGridColumn.useDiscountColumn(),
    dataGridColumn.useTotalNoVatColumn(),
    dataGridColumn.useTotalVatColumn(),
    dataGridColumn.useCurrencyColumn({ editors }),
    dataGridColumn.useExchangeRateColumn(),
    dataGridColumn.useExchangeRateDateColumn(),
    dataGridColumn.usepaidColumn(),
    dataGridColumn.useNotesColumn(),
    dataGridColumn.useVehicleNotesColumn(),
    dataGridColumn.usepaidAmountColumn(),
    {
      caption: i18n.t('common.priceVat', 2),
      dataField: 'includes_vat',
      dataType: 'boolean',
      visible: false,
    },
    {
      caption: i18n.t('common.expectedDeliveryDate'),
      dataField: 'expected_delivery_date',
      dataType: 'date',
      visible: false,
    },
  ]
}
